import React, { useState, useEffect, useRef } from "react";
import { Alert } from "react-bootstrap";
import { showToast } from "@/api/notification";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";
import { useApiCall } from "@/hooks/useApiCall";
import LoadingComponent from "@/components/Loading";

interface CoverImage {
  landing_page_id: string;
  is_for_mobile?: boolean;
  is_for_desktop?: boolean;
  image?: string;
  created_at?: string;
}
interface coverPut {
  path: string;
  id: string;
  body?: Record<string, unknown> | FormData;
}

const Website_Cover_Photo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [imageList, setImageList] = useState<CoverImage[]>([]);
  const [selectedType, setSelectedType] = useState<"desktop" | "mobile">(
    "desktop"
  );
  const [coverImageLoading, setCoverImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { postApi, getApi } = useGeneralApiCall();
  const { put } = useApiCall();

  useEffect(() => {
    fetchImages();
  }, []);

  const handleTypeChange = (type: "desktop" | "mobile") => {
    setSelectedImage(null);
    setSelectedType(type);
    setCoverImageLoading(true);
  };

  const fetchImages = async () => {
    try {
      const response: any = await getApi("/api/landing/v1/landing-page/");
      if (response?.data) {
        const sortedData = response.data.sort(
          (a: CoverImage, b: CoverImage) =>
            new Date(b.created_at || "").getTime() -
            new Date(a.created_at || "").getTime()
        );
        setImageList(sortedData);
      }
    } catch (err) {
      showToast({ message: "Failed to fetch images", type: "error" });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const width = img.width;
        const height = img.height;

        const isLandscape = 1.4 <= width / height && width / height <= 1.8;
        const isPortrait = 0.5 <= width / height && width / height <= 0.62;

        if (selectedType === "desktop" && isPortrait) {
          showToast({
            message: "Please upload a landscape image for desktop view",
            type: "error",
          });
          setSelectedImage(null);
          event.target.value = ""; // Clear the input
          return;
        } else if (selectedType === "mobile" && isLandscape) {
          showToast({
            message: "Please upload a portrait image for mobile view",
            type: "error",
          });
          setSelectedImage(null);
          event.target.value = ""; // Clear the input
          return;
        }

        setSelectedImage(file);
      };
      img.src = e.target?.result as string;
    };

    if (file?.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      showToast({ message: "Please upload a valid image file", type: "error" });
      setSelectedImage(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) {
      showToast({ message: "Please select an image first", type: "error" });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("image", selectedImage);
    formData.append(
      "is_for_mobile",
      selectedType === "mobile" ? "true" : "false"
    );
    formData.append(
      "is_for_desktop",
      selectedType === "desktop" ? "true" : "false"
    );

    try {
      const response = await postApi("/api/landing/v1/landing-page/", formData);
      if (response?.data) {
        showToast({ message: "Image uploaded successfully!", type: "success" });
        setSelectedImage(null);
        fetchImages();
      } else {
        showToast({ message: "Image upload failed", type: "error" });
      }
    } catch {
      showToast({ message: "Something went wrong", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const makeCoverImage = async (landing_page_id: string) => {
    try {
      setLoading(true);
      const payload =
        selectedType === "desktop"
          ? { is_for_desktop: true }
          : { is_for_mobile: true };
      const response = await put<coverPut>(
        `/api/landing/v1/landing-page/${landing_page_id}/make-cover/`,
        payload
      );
      if (response?.data) {
        showToast({
          message: "This image set as a website cover image",
          type: "success",
        });
        fetchImages();
      } else {
        showToast({ message: "Failed to update cover image", type: "error" });
      }
    } catch {
      showToast({
        message: "Error occurred while updating cover image",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = imageList.filter((img) =>
    selectedType === "desktop" ? img.is_for_desktop : img.is_for_mobile
  );

  return (
    <>
      {loading ? (
        <LoadingComponent />
      ) : (
        <>
          <Alert className="text-center">WEBSITE COVER PHOTO</Alert>
          <div className="flex flex-col gap-3">
            <div className="flex col-span-4">
              <div className="flex flex-col mb-4">
                <div className="flex justify-center mb-3 gap-3">
                  <button
                    className={`text-white px-4 py-2 text-base !rounded-md ${
                      selectedType === "desktop"
                        ? "!bg-blue-500"
                        : "!text-gray-600 !outline-blue-500 outline-1 hover:!bg-blue-500 hover:!text-white"
                    } 3xl:!px-8 3xl:!py-4 3xl:!text-xl`}
                    onClick={() => handleTypeChange("desktop")}
                  >
                    For Desktop
                  </button>
                  <button
                    className={`text-gray-500 px-4 py-2 text-base !rounded-md ${
                      selectedType === "mobile"
                        ? "!bg-gray-500 text-white"
                        : " !outline-gray-500 outline-1 hover:!bg-gray-500 hover:!text-white"
                    } 3xl:!px-8 3xl:!py-4 3xl:!text-xl`}
                    onClick={() => handleTypeChange("mobile")}
                  >
                    For Mobile
                  </button>
                </div>

                <div className="grid grid-cols-5 gap-3">
                  {/* Upload Dropzone */}
                  <div
                    className={`${
                      selectedType === "desktop" ? "col-span-3" : "col-span-[4]"
                    }`}
                  >
                    <div
                      className={`mb-3 ${
                        selectedType === "desktop" ? "h-[21rem]" : "h-[27rem]"
                      }`}
                      style={{
                        border: "2px dashed #aaa",
                        cursor: "pointer",
                        textAlign: "center",
                        borderRadius: "10px",
                        backgroundColor: "#f9f9f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => inputRef.current?.click()}
                    >
                      <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileSelect(e)}
                      />
                      {!selectedImage ? (
                        <p>
                          Click to select an image for{" "}
                          <strong>{selectedType}</strong>
                        </p>
                      ) : (
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Selected Preview"
                          className="my-4 mx-2 h-full rounded-2xl"
                        />
                      )}
                    </div>

                    <div className="text-center flex gap-4 justify-center">
                      <button
                        className={`btn ${
                          selectedImage ? "btn-danger" : "btn-secondary"
                        }`}
                        onClick={() => setSelectedImage(null)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={handleUpload}
                        disabled={loading}
                      >
                        Upload Selected Image
                      </button>
                    </div>
                  </div>

                  {/* Selected Cover Image */}
                  <div
                    className={`${
                      selectedType === "desktop" ? "col-span-2" : "col-span-[1]"
                    }`}
                  >
                    {filteredImages.length === 0 ? (
                      <div className="text-center">
                        <p>No images uploaded for {selectedType} view.</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        {coverImageLoading && (
                          <div className="flex justify-center items-center h-[200px]">
                            <LoadingComponent />
                          </div>
                        )}
                        <img
                          src={filteredImages[0]?.image}
                          alt="Cover"
                          className={`${
                            selectedType === "desktop"
                              ? "preview-image-desktop-cover"
                              : "preview-image"
                          } rounded-[5px]`}
                          style={{
                            display: coverImageLoading ? "none" : "block",
                          }}
                          onLoad={() => setCoverImageLoading(false)}
                          onError={() => setCoverImageLoading(false)}
                        />
                        {!coverImageLoading && (
                          <p className="mt-2 text-success fw-bold">
                            ✅ This is the current cover image
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Gallery Display */}
            <div className="flex flex-col">
              <h5 className="text-center !text-[30px] mb-3">
                {`Previous 
                ${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
                Cover Images`}
              </h5>
              <div className="flex flex-wrap gap-3">
                {filteredImages.length > 0 ? (
                  filteredImages.slice(1).map((image) => (
                    <div
                      key={image.landing_page_id}
                      // className="col-12 col-md-6 mb-4"
                      className="flex flex-wrap"
                    >
                      <div className="text-center">
                        <img
                          src={image.image}
                          alt="Cover"
                          // className="preview-image"
                          className="preview-image-desktop"
                        />

                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={() => makeCoverImage(image.landing_page_id)}
                        >
                          Make as Cover Image
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted">
                    No images uploaded for {selectedType} view.
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Website_Cover_Photo;
